import React, { useState } from 'react';
import { Button, Typography, Modal, useToast } from '@crisellevaldez/speculo-ui';
import { Copy, Server } from 'lucide-react';

interface DnsRecord {
  type: string;
  host: string;
  data: string;
  label?: string;
}

interface DnsRecordsDisplayProps {
  /** IDX subdomain (e.g., 'homes') */
  idxSubdomain?: string;
  /** Whether to show as a button that opens a modal, or inline */
  variant?: 'modal' | 'inline';
  /** Custom button label */
  buttonLabel?: string;
}

// Static DNS records for email configuration
const EMAIL_DNS_RECORDS: DnsRecord[] = [
  {
    type: 'CNAME',
    host: 's1._domainkey.aimail',
    data: 's1.domainkey.u47933949.wl200.sendgrid.net',
    label: 'DomainKey 1',
  },
  {
    type: 'CNAME',
    host: 'mail.aimail',
    data: 'u47933949.wl200.sendgrid.net',
    label: 'Mail',
  },
  {
    type: 'CNAME',
    host: 's2._domainkey.aimail',
    data: 's2.domainkey.u47933949.wl200.sendgrid.net',
    label: 'DomainKey 2',
  },
  {
    type: 'TXT',
    host: '_dmarc',
    data: 'v=DMARC1; p=none',
    label: 'DMARC',
  },
];

// Single DNS record row component
const DnsRecordRow: React.FC<{
  record: DnsRecord;
  onCopy: (text: string, label: string) => void;
}> = ({ record, onCopy }) => (
  <div className="border-t dark:border-white/10 pt-3 first:border-t-0 first:pt-0">
    {record.label && (
      <Typography variant="h3" className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
        {record.label}
      </Typography>
    )}
    <div className="text-sm space-y-2">
      <div className="grid grid-cols-[60px_1fr_auto] items-center gap-2">
        <span className="font-semibold text-xs">Type:</span>
        <span className="text-xs">{record.type}</span>
        <div className="w-8"></div>
      </div>
      <div className="grid grid-cols-[60px_1fr_auto] items-center gap-2">
        <span className="font-semibold text-xs">Host:</span>
        <code className="bg-white dark:bg-white/10 px-2 py-1 rounded text-xs truncate">{record.host}</code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onCopy(record.host, 'Host')}
        >
          <Copy className="w-3 h-3" />
        </Button>
      </div>
      <div className="grid grid-cols-[60px_1fr_auto] items-center gap-2">
        <span className="font-semibold text-xs">Data:</span>
        <code className="bg-white dark:bg-white/10 px-2 py-1 rounded text-xs truncate">{record.data}</code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onCopy(record.data, 'Data')}
        >
          <Copy className="w-3 h-3" />
        </Button>
      </div>
    </div>
  </div>
);

// DNS Records content (used by both modal and inline)
const DnsRecordsContent: React.FC<{
  idxSubdomain?: string;
  onCopy: (text: string, label: string) => void;
}> = ({ idxSubdomain, onCopy }) => (
  <div className="space-y-4">
    {/* IDX CNAME Record */}
    {idxSubdomain && (
      <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-lg space-y-3">
        <Typography variant="h3" className="text-sm font-semibold text-foreground">
          CNAME Record (IDX Site)
        </Typography>
        <DnsRecordRow
          record={{
            type: 'CNAME',
            host: idxSubdomain,
            data: 'aisearch.web.app',
          }}
          onCopy={onCopy}
        />
      </div>
    )}

    {/* Email DNS Records */}
    <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-lg space-y-4">
      <Typography variant="h3" className="text-sm font-semibold text-foreground">
        Email DNS Records
      </Typography>
      {EMAIL_DNS_RECORDS.map((record, index) => (
        <DnsRecordRow key={index} record={record} onCopy={onCopy} />
      ))}
    </div>

    <p className="text-xs text-gray-500 dark:text-gray-400">
      Add these DNS records to your domain registrar. Propagation can take up to 24 hours.
    </p>
  </div>
);

/**
 * Reusable DNS Records display component.
 * Can be used as a modal button or inline display.
 */
export const DnsRecordsDisplay: React.FC<DnsRecordsDisplayProps> = ({
  idxSubdomain,
  variant = 'modal',
  buttonLabel = 'View DNS Records',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { showToast } = useToast();

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast({
      message: `${label} copied to clipboard`,
      variant: 'success',
    });
  };

  if (variant === 'inline') {
    return <DnsRecordsContent idxSubdomain={idxSubdomain} onCopy={handleCopy} />;
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-2"
      >
        <Server className="w-4 h-4" />
        {buttonLabel}
      </Button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
        <Modal.Header>
          <Typography variant="h3">DNS Records</Typography>
        </Modal.Header>
        <Modal.Body className="py-6">
          <DnsRecordsContent idxSubdomain={idxSubdomain} onCopy={handleCopy} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
